import React, { ReactNode, useEffect, useRef, useState } from 'react'
import Icon from '../Icon2/Icon';
import Menu from '../Menu/Menu';
import { IMenu } from '../Menu/Menu';
import AsyncSelect, { LoadMoreArgsProps, LoadMoreOptReturnProps } from '../Select/AsyncSelect';
import Button from '../Button/Button';
import './TopNavBar.css';
import { API_MAP } from './utils';

export enum DropdownItemType {
    REDIRECT = 'REDIRECT',
    ACTION = 'ACTION',
    ASYNC_DROPDOWN = 'ASYNC_DROPDOWN',
    DROPDOWN = 'DROPDOWN'
}

export interface NavBarItemProps {
    displayName: string;
    type: "REDIRECT" | "DROPDOWN" | "ACTION" | "ASYNC_DROPDOWN";
    action?: string;
    icon?: string;
    iconVersion?: string;
    dynamicMenuItem?: boolean;
    redirectUrl?: string;
    hotKey?: string;
    trialExpired?: boolean;
    testId?: string;
    isActive?: boolean;
    subMenu?: NavBarItemProps[];
    id?: string;
}

interface onAction {
    action: string,
    displayName: string,
    icon: string,
    type: string,
}

export interface NavBarItemsProps {
    navbarItems: NavBarItemProps[],
    onNavigate: (url: string) => void,
    dynamicNavItemsWidth: number,
    loadMoreOptions?: (loadmoreArgs: LoadMoreArgsProps) => Promise<LoadMoreOptReturnProps>,
    pathName?: string,
    onAction?: (props: onAction) => void,
}

interface ExtendedLoadMoreArgsProps extends LoadMoreArgsProps {
    itemId: keyof typeof API_MAP;
}

const NavBarItems = ({ navbarItems, onNavigate, dynamicNavItemsWidth, loadMoreOptions, pathName, onAction }: NavBarItemsProps): JSX.Element => {
    const [visibleItems, setVisibleItems] = useState<NavBarItemProps[]>(navbarItems);
    const [truncateItems, setTruncateItems] = useState<NavBarItemProps[]>([]);
    const navVisibleItemsContainerRef = useRef<HTMLDivElement>(null);
    const safePathName = pathName ?? '';

    // For AsyncSelct : 
    const [value, updateValue] = useState(null)
    const [numberOfRequests, setNumberOfRequests] = useState<number>(0)

    //To render all items, basically get a default view of all items
    const renderAllItems = () => {
        setVisibleItems(navbarItems);
        setTruncateItems([]);
    }

    useEffect(() => {
        //Get the default view of all items
        renderAllItems();

        // Calculate the visibility of items on window resize by observing the container
        const observer = new ResizeObserver(() => {
            const navbarItemsLength = navbarItems.length;
            if (navVisibleItemsContainerRef.current.children.length === navbarItemsLength) {
                calculateDynamicItemVisibility();
            } else {
            }

        })

        // Observe the container for changes to its child elements
        observer.observe(navVisibleItemsContainerRef?.current);

        // Cleanup function: clear the observer on unmount
        return () => {
            observer.disconnect();
        };

    }, [dynamicNavItemsWidth, navbarItems, navVisibleItemsContainerRef]);

    // Calculate the visibility of items based on the width of the dynamicNavItems
    const calculateDynamicItemVisibility = () => {
        // If the ref is not set, return
        if (!navVisibleItemsContainerRef.current) return;

        //Declare variables
        let cumulativeWidth = 0;
        let visibleCount = 0;
        const truncateButtonWidth = 72 // Width of the truncate button

        // Calculate the width of the dynamicNavItems container and the sum of the visible children
        const dynamicNavItemsWidthRounded = Math.round(dynamicNavItemsWidth)
        const visibleChildrenWidths: number[] = Array.from(navVisibleItemsContainerRef?.current.children).map(
            (child) => (child as HTMLElement).offsetWidth
        );

        // Calculate the available width for visible items (leave space for the truncation button)
        const availableWidth = dynamicNavItemsWidthRounded - truncateButtonWidth;

        // Calculate how many items fit within availableWidth
        for (let i = 0; i < visibleChildrenWidths?.length; i++) {
            if (cumulativeWidth + visibleChildrenWidths[i] <= availableWidth) {
                cumulativeWidth += visibleChildrenWidths[i];
                visibleCount++;
            } else {
                break;
            }
        }

        // Split items into visible and truncated items
        const visibleItems = navbarItems?.slice(0, visibleCount);
        const truncateItems = navbarItems?.slice(visibleCount);

        // Update state with visible and truncated items
        setVisibleItems(visibleItems);
        setTruncateItems(truncateItems);
    };

    return (
        <div
            className="TopNavbar__content__items"
            id='navbar-items-wrapper-id'
        >
            {/* Render visible items */}
            <div
                ref={navVisibleItemsContainerRef}
                className="TopNavbar__content__items__list flex-v-center"
                id='cs-visible-item-container-id'
                key={navbarItems.length}
            >
                {visibleItems.map((item: any) => {
                    switch (item.type) {
                        case DropdownItemType.REDIRECT:
                            return NavbarItem(item, onNavigate, safePathName);
                        case DropdownItemType.DROPDOWN:
                            return NavbarDropdown(item, onNavigate, safePathName, onAction, value, updateValue, numberOfRequests, setNumberOfRequests, loadMoreOptions);
                        case DropdownItemType.ASYNC_DROPDOWN:
                            return <NavbarAsyncDropdown
                                menuItem={item}
                                onNavigate={onNavigate}
                                value={value}
                                updateValue={updateValue}
                                numberOfRequests={numberOfRequests}
                                setNumberOfRequests={setNumberOfRequests}
                                loadMoreOptions={loadMoreOptions}
                                item={item}
                            />;
                        default:
                            return null;
                    }
                })}
            </div>
            {/* Render More (truncate) dropdown if there are hidden items */}
            {dynamicNavItemsWidth ? (truncateItems.length > 0 && NavbarTruncateDropdown(truncateItems, onNavigate, visibleItems.length, onAction, value, updateValue, numberOfRequests, setNumberOfRequests, loadMoreOptions)) : null}
        </div>
    );
};

const redirectButtonIconProps = {
    size: 'small',
    style: {
        marginRight: '0.25rem'
    },
    version: 'v2'
}

const redirectMenuButtonIconProps = (visibleItemsCountHelper: boolean) => {
    const styleUpdate = { ...redirectButtonIconProps.style };
    if (visibleItemsCountHelper) {
        styleUpdate.marginRight = '0';
        return {
            ...redirectButtonIconProps,
            style: styleUpdate
        };
    } else {
        return redirectButtonIconProps;
    }
}

const NavbarItem = (item: NavBarItemProps, onNavigate: (url: string) => void, safePathName: string): JSX.Element => {

    const handleClick = (e) => {
        e.currentTarget.blur();
        if (onNavigate) {
            onNavigate(item.redirectUrl); // Invoke the callback for navigation
        }
    };

    const isSelected =
        item.id !== undefined &&
            safePathName !== undefined &&
            item.id === safePathName
            ? '__isSelected'
            : '';

    return (
        <div
            className='TopNavbar__content__items__list__redirect'
            tabIndex={1}
        >
            <Button
                className={`TopNavbar__content__items__list__redirect--button${isSelected}`}
                version='v2'
                aria-label={item.displayName}
                {...(item.icon && { icon: item.icon })}
                iconProps={redirectButtonIconProps}
                buttonType='tertiary' TopNavbar__content__items__lists__button__wrapper
                size='small'
                onClick={handleClick}
                testId={item.testId}
                tabIndex={0}
            >
                {item.displayName}
            </Button>
        </div>
    )
}

const NavbarDropdown = (
    menuItem: NavBarItemProps,
    onNavigate: (url: string) => void,
    safePathName: string,
    onAction: (props: onAction) => void,
    value: any,
    updateValue: (data: any) => void,
    numberOfRequests: number,
    setNumberOfRequests: (count: number) => void,
    loadMoreOptions: (loadmoreArgs: ExtendedLoadMoreArgsProps) => Promise<LoadMoreOptReturnProps>,
): JSX.Element => {

    const handleRedirect = (e: any) => {
        e.currentTarget.blur();
        if (menuItem.redirectUrl) {
            onNavigate(menuItem.redirectUrl);
        }
    };
    const isSelected =
        menuItem.id !== undefined &&
            safePathName !== undefined &&
            menuItem.id === safePathName
            ? '__isSelected'
            : '';

    return (
        <div className='TopNavbar__content__items__list__dropdown flex-v-center'>
            <Menu
                version='v2'
                type="hover"
                list={
                    menuItem.subMenu?.map(item => {
                        const subMenuItem: IMenu = {
                            label: item?.displayName,
                            testId: item?.testId,
                            key: item?.displayName.toLowerCase().replace(" ", "-"),
                            action: (): void => {
                                if (item.type === DropdownItemType.ACTION) {
                                    onAction(item as any)
                                } else if (item.type === DropdownItemType.REDIRECT) {
                                    onNavigate(item.redirectUrl)
                                }
                            }
                        }
                        if (item.subMenu) {
                            subMenuItem.subList = item.subMenu.map(subItem => ({
                                label: subItem?.displayName,
                                action: (): void => {
                                    if (subItem.type === DropdownItemType.ACTION) {
                                        onAction(subItem as onAction)
                                    } else if (subItem.type === DropdownItemType.REDIRECT) {
                                        onNavigate(subItem.redirectUrl)
                                    }
                                },
                                data: { width: 400, height: 800 },
                                testId: subItem?.testId,
                                key: subItem?.displayName.toLowerCase().replace(" ", "-"),
                            }))
                            subMenuItem.postFixComponent = (): JSX.Element => (
                                <Icon icon="CaretRight" version='v2' className="rightArrow" size="small" />);
                        }

                        if (item.type === DropdownItemType.ACTION) {
                            subMenuItem.action = (): void => onAction(item as onAction);
                            subMenuItem.data = { width: 400, height: 800 };
                        }

                        if (item.type === DropdownItemType.ASYNC_DROPDOWN) {
                            subMenuItem.label = (
                                <NavbarAsyncDropdown
                                    menuItem={item}
                                    onNavigate={onNavigate}
                                    value={value}
                                    updateValue={updateValue}
                                    numberOfRequests={numberOfRequests}
                                    setNumberOfRequests={setNumberOfRequests}
                                    loadMoreOptions={loadMoreOptions}
                                    isPartOfMenu={true}
                                    item={item}
                                />
                            ) as ReactNode;
                            subMenuItem.postFixComponent = (): JSX.Element => (
                                <Icon icon="CaretRight" version='v2' className="rightArrow" size="small" />);
                            subMenuItem.subList = null;
                        }
                        return subMenuItem;
                    }) ?? []}
                subMenuPosition='right'
            >
                <Button
                    version='v2'
                    className={`${menuItem.id} TopNavbar__content__items__list__dropdown--button${isSelected}`}
                    {...(menuItem.icon && { icon: menuItem.icon })}
                    iconProps={redirectButtonIconProps}
                    testId={menuItem.testId}
                    buttonType='tertiary'
                    size='small'
                    onClick={handleRedirect}
                >
                    {menuItem.displayName}
                    <Icon
                        className={`${menuItem.id} TopNavbar__content__items__list__dropdown--button${isSelected}--Icon`}
                        version="v2"
                        icon="CaretDownNew"
                        height="16px"
                        width="16px"
                        size='tiny'
                    />
                </Button>
            </Menu>
        </div >

    )
}

const NavbarTruncateDropdown = (
    menuItem: NavBarItemProps[],
    onNavigate: (url: string) => void,
    visibleItems: number,
    onAction?: (props: onAction) => void,
    value?: any,
    updateValue?: (data: any) => void,
    numberOfRequests?: number,
    setNumberOfRequests?: (count: number) => void,
    loadMoreOptions?: (loadmoreArgs: ExtendedLoadMoreArgsProps) => Promise<LoadMoreOptReturnProps>
): JSX.Element => {

    const visibleItemsCountHelper = visibleItems <= 3;

    const truncatedItems: IMenu[] = menuItem.map((item) => {

        // const navItemSubList = item.subMenu?.map((subItem) => ({
        //     label: subItem.displayName,
        //     action: (): void => subItem.redirectUrl ? onNavigate(subItem.redirectUrl) : null,
        //     postFixComponent: subItem.subMenu ? () => <Icon icon="Dashboard" version='v2' size="small" /> : null,
        //     testId: subItem.testId,
        //     key: subItem.displayName.toLowerCase().replace(" ", "-")
        // }));

        const subMenuNavItem: IMenu = {
            label: (
                <>
                    {item.icon && <Icon icon={item.icon} version='v2' size="small" style={{ marginRight: '0.25rem' }} />}
                    {item.displayName}
                </>
            ),
            action: (): void => item.redirectUrl ? onNavigate(item.redirectUrl) : null,
            subList: null,
            //postFixComponent: item.subMenu ? () => <Icon icon="CaretRight" version='v2' size="small" /> : null,
            testId: item.testId,
            key: item.displayName.toLowerCase().replace(" ", "-")
        }

        if (item.type === DropdownItemType.ASYNC_DROPDOWN) {
            subMenuNavItem.label = (
                <NavbarAsyncDropdown
                    menuItem={item}
                    onNavigate={onNavigate}
                    value={value}
                    updateValue={updateValue}
                    numberOfRequests={numberOfRequests}
                    setNumberOfRequests={setNumberOfRequests}
                    loadMoreOptions={loadMoreOptions}
                    isPartOfMenu={true}
                    isTruncated={true}
                    item={item}
                />
            ) as ReactNode,
            subMenuNavItem.postFixComponent = () => <Icon icon="CaretRight" version='v2' className="rightArrow" size="small" />,
            subMenuNavItem.subList = null;
        }
        return subMenuNavItem;

    });

    return (
        <div className='TopNavbar__content__items__truncate__wrapper'>
            <Menu
                version="v2"
                type="hover"
                list={truncatedItems}
                subMenuPosition="right"
            >
                <Button
                    version="v2"
                    className={`TopNavbar__content__items__truncate__wrapper__button`}
                    icon="SeeMore"
                    iconProps={redirectMenuButtonIconProps(visibleItemsCountHelper)}
                    testId={"cs-dropdown-truncate-button"}
                    buttonType="tertiary"
                    size="small"
                >
                    {(visibleItemsCountHelper) ? "" : "More"}
                </Button>
            </Menu>
        </div>)
}

const NavbarAsyncDropdown: React.FC<{
    menuItem: NavBarItemProps;
    onNavigate: (url: string) => void;
    value: any;
    updateValue: (data: any) => void;
    numberOfRequests: number;
    setNumberOfRequests: (count: number) => void;
    loadMoreOptions: (loadmoreArgs: ExtendedLoadMoreArgsProps) => Promise<LoadMoreOptReturnProps>;
    isPartOfMenu?: boolean;
    isTruncated?: boolean;
    item: any;
}> = ({
    menuItem,
    onNavigate,
    value,
    updateValue,
    numberOfRequests,
    setNumberOfRequests,
    loadMoreOptions,
    isPartOfMenu = false,
    isTruncated = false,
    item,
}): JSX.Element => {
        const isPartOfMenuClass = isPartOfMenu ? '--partOfMenu' : '';

        const handleRedirect = () => {
            if (menuItem.redirectUrl) {
                onNavigate(menuItem.redirectUrl);
            }
        };

        let pageSize = 10;

        const handleValueUpdate = (data: any) => {
            data.value();
        };

        return (
            <div className={`TopNavbar__content__items__list__dropdown TopNavbar__content__items__list__async__dropdown${isPartOfMenuClass} flex-v-center`}>
                <AsyncSelect
                    value={value}
                    onChange={handleValueUpdate}
                    openOnHover={true}
                    hasIcon={true}
                    placeholder={(
                        <>
                            <Button
                                version='v2'
                                className={`${menuItem.id} TopNavbar__content__items__list__async__dropdown__button${isPartOfMenuClass}`}
                                icon={menuItem.icon}
                                iconProps={redirectButtonIconProps}
                                testId={menuItem.testId}
                                buttonType='tertiary'
                                size='small'
                                onClick={handleRedirect}
                            >
                                {menuItem.displayName}
                                {!isPartOfMenu &&
                                    <Icon
                                        className={`TopNavbar__content__items__list__async__dropdown__button--Icon${isPartOfMenuClass}`}
                                        version="v2"
                                        icon="CaretDownNew"
                                        height="16px"
                                        width="16px"
                                        size='tiny'
                                    />
                                }
                            </Button>
                        </>
                    ) as unknown as string}
                    loadMoreOptions={(params) => loadMoreOptions({ ...params, itemId: item.id })}
                    limit={pageSize}
                    width="200px"
                    isClearable
                    defaultOptions={false}
                    debounceTimeout={0}
                    version='v2'
                    styles={{
                        menuPortal: (base: any): any => ({
                            ...base,
                            zIndex: 9999,
                            marginTop: '-0.563rem',
                            marginLeft: '-0.063rem',
                        }),
                        menu: (base: any) => ({
                            ...base,
                            left: isTruncated ? '14.02rem' : isPartOfMenu ? '14.313rem' : 'initial',
                            top: isPartOfMenu ? '-2.25rem' : 'initial',
                            width: isPartOfMenu ? '10.25rem' : 'initial',
                            boxShadow: "0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 3px rgba(0, 0, 0, 0.12), 0px 4px 15px 0px rgba(108, 92, 231, 0.20) !important",
                            padding: '0.5rem 0rem',
                            '[class$="-MenuList"]': {
                                maxHeight: "19.313rem !important",
                            },
                            marginLeft: !isPartOfMenu ? '0 !important' : 'undefined',
                        }),
                        menuList: (base) => ({
                            ...base,
                            "& .Portal__option": {

                                color: "#212121",
                                "fontSize": ".875rem !important",
                                "height": "2.5rem !important",
                                "display": "flex",
                                "alignItems": "center",

                                "> div": {
                                    "width": "inherit",
                                },
                                "&:hover": {
                                    background: "#6c5ce70d !important",
                                    color: "#5a20b9 !important",
                                },
                                "&--is-focused": {
                                    backgroundColor: "transparent !important",
                                    color: "inherit !important",
                                },
                                "& .tippy-wrapper": {
                                    width: "100% !important",
                                },
                            },
                        }),

                    }}
                    menuPortalTarget={document.body}
                />
            </div>
        );
    };

export default NavBarItems;